import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, tokenStore } from '../api/client'

export interface WorkPoint { id: string; code: string; name: string }

export interface CurrentUser {
  id: string
  userName: string
  email: string
  fullName: string
  language: string
  mustChangePassword: boolean
  workPointId: string | null
  workPointName: string | null
  workPoints: WorkPoint[]
  roles: string[]
  permissions: string[]
}

interface AuthState {
  user: CurrentUser | null
  loading: boolean
  login: (userNameOrEmail: string, password: string) => Promise<void>
  logout: () => Promise<void>
  logoutAll: () => Promise<void>
  switchWorkPoint: (workPointId: string) => Promise<void>
  /** Verificare de permisiune pentru afișare. Serverul verifică din nou — NFR-SEC-003. */
  can: (permission: string) => boolean
  /**
   * Adoptă o sesiune deja stabilită în altă parte (ex. după acceptarea unei
   * invitații sau resetarea parolei, unde tokenStore e deja populat) — evită
   * un reload de pagină doar ca AuthContext să afle că userul e autentificat.
   */
  adoptSession: (user: CurrentUser) => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  // La încărcarea aplicației, access token-ul din memorie s-a pierdut (refresh de
  // pagină). Cookie-ul de refresh a supraviețuit, deci sesiunea se poate relua.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.post<{ accessToken: string; user: CurrentUser }>('/auth/refresh', {})
        if (cancelled) return
        tokenStore.set(data.accessToken)
        setUser(data.user)
      } catch {
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    tokenStore.onLost(() => setUser(null))
  }, [])

  const login = useCallback(async (userNameOrEmail: string, password: string) => {
    const { data } = await api.post<{ accessToken: string; user: CurrentUser }>('/auth/login', {
      userNameOrEmail, password,
    })
    tokenStore.set(data.accessToken)
    setUser(data.user)
  }, [])

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout') } finally {
      tokenStore.set(null)
      setUser(null)
    }
  }, [])

  const logoutAll = useCallback(async () => {
    try { await api.post('/auth/logout-all') } finally {
      tokenStore.set(null)
      setUser(null)
    }
  }, [])

  const switchWorkPoint = useCallback(async (workPointId: string) => {
    const { data } = await api.post<{ accessToken: string; user: CurrentUser }>('/auth/refresh', { workPointId })
    tokenStore.set(data.accessToken)
    setUser(data.user)
  }, [])

  const can = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  )

  const adoptSession = useCallback((u: CurrentUser) => setUser(u), [])

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, logoutAll, switchWorkPoint, can, adoptSession }),
    [user, loading, login, logout, logoutAll, switchWorkPoint, can, adoptSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth trebuie folosit în interiorul AuthProvider.')
  return ctx
}
